import React from 'react';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';
import { Users, Award, Globe, TrendingUp } from 'lucide-react';

const About = () => {

  const values = [
    { 
      title: 'Vision',
      description: (
        <>
          To empower a future where software is meticulously crafted with integrity, agility, and intelligent adaptation transforming complexity into clarity through the fusion of human ingenuity and AI.
          <br />          
          Argorine envisions a world where every digital solution is a reflection of purpose, progress, and precision.
        </>
      )
    },
    {
      title: 'Mission',
      description: 'Deliver smart, resilient software through mindful design, AI-driven agility, and purposeful innovation that drives progress.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            About <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Argorine Solutions</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Founded in 2024, Argorine Solutions has been at the forefront of digital innovation, helping businesses transform their operations through technology.
          </motion.p>
        </AnimatedSection>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Content */}
          <AnimatedSection className="space-y-6">
            <motion.h3 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl font-bold text-gray-900"
            >
              Empowering Businesses Through Technology
            </motion.h3>
            <motion.p 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              At Argorine Solutions, we believe that technology should be an enabler, not a barrier. Our team of experienced architects,developers, and consultants work closely with clients to understand their unique challenges and deliver solutions that drive real business value.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-gray-600 leading-relaxed"
            >
              From startups to enterprise organizations, we've helped companies across various industries leverage technology to streamline operations, enhance customer experiences, and accelerate growth.
            </motion.p>
            
            
          </AnimatedSection>

          {/* Right Content - Image */}
          <AnimatedSection direction="right" className="relative">
            <motion.img
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800"
              alt="Team collaboration"
              className="rounded-2xl shadow-xl"
            />
          </AnimatedSection>
        </div>


        {/* Values */}
        <div>
          <AnimatedSection>
            <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Core Values</h3>
          </AnimatedSection>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;